import { useEffect, useState } from "react";
import { cleanMoneyInput, cleanSignedMoneyInput, blockInvalidNumberKeys } from "../utils/numberInput";
import { sanitizeText, sanitizeTextInput, sanitizeNoteInput, sanitizeDate } from "../utils/security";
import { getLocalVaultData, persistLocalVaultData } from "../utils/localVaultSecurity";
import { readLegacyString, removeLegacyItem } from "../utils/secureStore";

const STRATEGIES = ["Breakout","Trend Follow","Mean Reversion","Scalping","Swing","News Play","Options Strategy","Other"];
const EMOTIONS   = [
  {e:"😊",l:"Confident"},{e:"😰",l:"Anxious"},{e:"😤",l:"FOMO"},
  {e:"😌",l:"Calm"},{e:"😢",l:"Regret"},{e:"🤑",l:"Greedy"},
  {e:"😎",l:"In control"},{e:"😵",l:"Confused"}
];

const JOURNAL_KEY = "tradeJournal";
const LEGACY_JOURNAL_KEY = "finvault_journal";

function sanitizeJournalEntry(entry = {}) {
  return {
    id: sanitizeText(entry.id, 120),
    createdAt: Number(entry.createdAt || Date.now()) || Date.now(),
    date: sanitizeDate(entry.date) || new Date().toISOString().split("T")[0],
    symbol: sanitizeText(entry.symbol, 20).toUpperCase(),
    strategy: STRATEGIES.includes(entry.strategy) ? entry.strategy : "Other",
    emotion: EMOTIONS.some(e => e.e === entry.emotion) ? entry.emotion : "😊",
    entry: cleanMoneyInput(entry.entry || ""),
    exit: cleanMoneyInput(entry.exit || ""),
    qty: cleanMoneyInput(entry.qty || ""),
    pnl: cleanSignedMoneyInput(entry.pnl || ""),
    notes: sanitizeText(entry.notes, 500),
    mistakes: sanitizeText(entry.mistakes, 500),
    lesson: sanitizeText(entry.lesson, 500),
    outcome: ["profit", "loss", "breakeven"].includes(entry.outcome) ? entry.outcome : "profit"
  };
}

async function loadJournals() {
  const vault = await getLocalVaultData();
  const existing = Array.isArray(vault?.[JOURNAL_KEY]) ? vault[JOURNAL_KEY] : [];
  if (existing.length) return existing.map(sanitizeJournalEntry);

  // One-time migration from the previous plain localStorage journal.
  let legacy = [];
  try { legacy = JSON.parse(readLegacyString(LEGACY_JOURNAL_KEY) || "[]") || []; } catch { legacy = []; }
  if (legacy.length) {
    const migrated = legacy.map(sanitizeJournalEntry);
    await saveJournals(migrated);
    removeLegacyItem(LEGACY_JOURNAL_KEY);
    return migrated;
  }
  return [];
}

async function saveJournals(list) {
  const vault = await getLocalVaultData();
  await persistLocalVaultData({ ...(vault || {}), [JOURNAL_KEY]: (list || []).map(sanitizeJournalEntry) });
}

export default function TradeJournal({ dark, tm }) {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    symbol:"", strategy:"Breakout", emotion:"😊",
    entry:"", exit:"", qty:"", pnl:"",
    notes:"", mistakes:"", lesson:"",
    outcome:"profit" // profit | loss | breakeven
  });

  useEffect(() => {
    let alive = true;
    loadJournals()
      .then(list => { if (alive) setJournals(list); })
      .catch(() => { if (alive) setJournals([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const today = d => new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
  const fmt   = n => "₹" + new Intl.NumberFormat("en-IN",{maximumFractionDigits:0}).format(Math.abs(n));

  const handleSave = async () => {
    if (!form.symbol.trim()) return;
    const entry = sanitizeJournalEntry({ ...form, id: Math.random().toString(36).slice(2,10), createdAt: Date.now() });
    const updated = [entry, ...journals];
    setJournals(updated);
    await saveJournals(updated);
    setShowForm(false);
    setForm({ date:new Date().toISOString().split("T")[0], symbol:"", strategy:"Breakout", emotion:"😊", entry:"", exit:"", qty:"", pnl:"", notes:"", mistakes:"", lesson:"", outcome:"profit" });
  };

  const handleDelete = async (id) => {
    const updated = journals.filter(j => j.id !== id);
    setJournals(updated);
    await saveJournals(updated);
    setSelected(null);
  };

  // Stats
  const profits = journals.filter(j=>j.outcome==="profit").length;
  const winRate = journals.length ? Math.round((profits/journals.length)*100) : 0;
  const totalPnL = journals.reduce((s,j)=>s+(parseFloat(j.pnl)||0),0);

  const inp = (label, key, type="text", placeholder="") => (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>{label}</div>
      <input type={type === "number" ? "text" : type} inputMode={type === "number" ? "decimal" : undefined} value={form[key]} onKeyDown={type === "number" ? (e)=>{ if (["e","E","+"].includes(e.key)) e.preventDefault(); } : undefined} onChange={e=>setForm(f=>({...f,[key]:type === "number" ? cleanSignedMoneyInput(e.target.value) : type === "date" ? sanitizeDate(e.target.value) : sanitizeTextInput(e.target.value, 120)}))}
        placeholder={placeholder}
        style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,
          background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
    </div>
  );

  return (
    <div style={{padding:"16px 16px 0"}}>

      {/* Stats row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
        {[
          {l:"Win Rate", v:`${winRate}%`, c:winRate>=50?"#16A34A":"#DC2626"},
          {l:"Total P&L", v:(totalPnL>=0?"+":"-")+fmt(totalPnL), c:totalPnL>=0?"#16A34A":"#DC2626"},
          {l:"Trades", v:journals.length, c:tm.text},
        ].map(s=>(
          <div key={s.l} style={{background:tm.card,borderRadius:16,padding:"12px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:10,color:tm.textSub,fontWeight:700,marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Add button */}
      <button onClick={()=>setShowForm(true)}
        style={{width:"100%",background:"#D97706",border:"none",borderRadius:16,padding:"14px",
          cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:800,fontSize:14,marginBottom:16,
          display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
        📓 + New Journal Entry
      </button>

      {/* Journal list */}
      {loading ? (
        <div style={{textAlign:"center",padding:"30px 20px",background:tm.card,borderRadius:20,color:tm.textSub,fontWeight:800}}>Loading journal…</div>
      ) : journals.length === 0 ? (
        <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
          <div style={{fontSize:40,marginBottom:10}}>📓</div>
          <div style={{color:tm.textSub,fontWeight:700,fontSize:14}}>No journal entries yet</div>
          <div style={{color:tm.textMuted,fontSize:12,marginTop:4}}>Record your trades to improve</div>
        </div>
      ) : journals.map(j => (
        <div key={j.id} onClick={()=>setSelected(j)}
          style={{background:tm.card,borderRadius:16,padding:"14px",marginBottom:10,cursor:"pointer",
            boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
            borderLeft:`4px solid ${j.outcome==="profit"?"#16A34A":j.outcome==="loss"?"#DC2626":"#F59E0B"}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{fontSize:22}}>{j.emotion}</span>
                <span style={{fontWeight:900,fontSize:15,color:tm.text}}>{j.symbol}</span>
                <span style={{fontSize:11,background:j.outcome==="profit"?"#DCFCE7":j.outcome==="loss"?"#FEE2E2":"#FEF3C7",
                  color:j.outcome==="profit"?"#16A34A":j.outcome==="loss"?"#DC2626":"#D97706",
                  fontWeight:700,padding:"2px 8px",borderRadius:999}}>{j.outcome.toUpperCase()}</span>
              </div>
              <div style={{fontSize:12,color:tm.textSub}}>{j.strategy} · {today(j.date)}</div>
            </div>
            {j.pnl && (
              <div style={{fontWeight:900,fontSize:16,
                color:parseFloat(j.pnl)>=0?"#16A34A":"#DC2626"}}>
                {parseFloat(j.pnl)>=0?"+":""}{fmt(parseFloat(j.pnl))}
              </div>
            )}
          </div>
          {j.notes && <div style={{fontSize:12,color:tm.textSub,marginTop:8,lineHeight:1.5,
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.notes}</div>}
        </div>
      ))}

      {/* Add Form Sheet */}
      {showForm && (
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div onClick={()=>setShowForm(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)"}}/>
          <div style={{position:"relative",background:tm.card,borderRadius:"24px 24px 0 0",
            padding:"0 20px 40px",maxHeight:"92vh",overflowY:"auto"}}>
            <div style={{width:40,height:4,background:tm.border,borderRadius:999,margin:"12px auto 20px"}}/>
            <div style={{fontSize:17,fontWeight:900,color:tm.text,marginBottom:20}}>📓 New Journal Entry</div>

            {inp("Symbol / Asset", "symbol", "text", "e.g. NIFTY, RELIANCE, BTC")}
            {inp("Date", "date", "date")}

            {/* Outcome */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:8}}>Outcome</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {["profit","loss","breakeven"].map(o=>(
                  <button key={o} onClick={()=>setForm(f=>({...f,outcome:o}))}
                    style={{padding:"10px",borderRadius:12,border:"none",cursor:"pointer",fontFamily:"inherit",
                      fontWeight:800,fontSize:12,
                      background:form.outcome===o?(o==="profit"?"#DCFCE7":o==="loss"?"#FEE2E2":"#FEF3C7"):"#F1F5F9",
                      color:form.outcome===o?(o==="profit"?"#16A34A":o==="loss"?"#DC2626":"#D97706"):tm.textSub}}>
                    {o==="profit"?"✅ Profit":o==="loss"?"❌ Loss":"➖ Breakeven"}
                  </button>
                ))}
              </div>
            </div>

            {/* Strategy */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:8}}>Strategy</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {STRATEGIES.map(s=>(
                  <button key={s} onClick={()=>setForm(f=>({...f,strategy:s}))}
                    style={{padding:"7px 14px",borderRadius:20,border:"none",cursor:"pointer",
                      fontFamily:"inherit",fontWeight:700,fontSize:12,
                      background:form.strategy===s?"#D97706":"#F1F5F9",
                      color:form.strategy===s?"white":tm.textSub}}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Emotion */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:8}}>Emotional State</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {EMOTIONS.map(({e,l})=>(
                  <button key={e} onClick={()=>setForm(f=>({...f,emotion:e}))}
                    style={{padding:"7px 12px",borderRadius:12,border:`2px solid ${form.emotion===e?"#D97706":tm.border}`,
                      cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:12,
                      background:form.emotion===e?"#FEF3C7":tm.input,color:tm.text}}>
                    {e} {l}
                  </button>
                ))}
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>Entry Price</div>
                <input type="text" inputMode="decimal" value={form.entry} onKeyDown={blockInvalidNumberKeys} onChange={e=>setForm(f=>({...f,entry:cleanMoneyInput(e.target.value)}))}
                  placeholder="₹" style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>Exit Price</div>
                <input type="text" inputMode="decimal" value={form.exit} onKeyDown={blockInvalidNumberKeys} onChange={e=>setForm(f=>({...f,exit:cleanMoneyInput(e.target.value)}))}
                  placeholder="₹" style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
              </div>
            </div>

            {inp("P&L Amount (use - for loss)", "pnl", "number", "e.g. 2500 or -1200")}

            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>Trade Notes</div>
              <textarea value={form.notes} maxLength={500} onChange={e=>setForm(f=>({...f,notes:sanitizeNoteInput(e.target.value,500)}))}
                placeholder="What happened? Why did you take this trade?"
                rows={3} style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,
                  background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none",resize:"none"}}/>
            </div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>Mistakes Made</div>
              <textarea value={form.mistakes} maxLength={500} onChange={e=>setForm(f=>({...f,mistakes:sanitizeNoteInput(e.target.value,500)}))}
                placeholder="What went wrong? What could be improved?"
                rows={2} style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,
                  background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none",resize:"none"}}/>
            </div>

            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>Lesson Learned</div>
              <textarea value={form.lesson} maxLength={500} onChange={e=>setForm(f=>({...f,lesson:sanitizeNoteInput(e.target.value,500)}))}
                placeholder="Key takeaway from this trade"
                rows={2} style={{width:"100%",padding:"11px 14px",borderRadius:12,border:`1.5px solid ${tm.border}`,
                  background:tm.input,color:tm.text,fontSize:14,fontFamily:"inherit",outline:"none",resize:"none"}}/>
            </div>

            <button onClick={handleSave}
              style={{width:"100%",padding:"16px",background:"#D97706",border:"none",borderRadius:16,
                cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:900,fontSize:16}}>
              Save Journal Entry
            </button>
          </div>
        </div>
      )}

      {/* Detail Sheet */}
      {selected && (
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div onClick={()=>setSelected(null)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)"}}/>
          <div style={{position:"relative",background:tm.card,borderRadius:"24px 24px 0 0",
            padding:"0 20px 40px",maxHeight:"88vh",overflowY:"auto"}}>
            <div style={{width:40,height:4,background:tm.border,borderRadius:999,margin:"12px auto 20px"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:28}}>{selected.emotion}</span>
                <div>
                  <div style={{fontSize:18,fontWeight:900,color:tm.text}}>{selected.symbol}</div>
                  <div style={{fontSize:12,color:tm.textSub}}>{selected.strategy} · {today(selected.date)}</div>
                </div>
              </div>
              <button onClick={()=>handleDelete(selected.id)}
                style={{background:"#FEE2E2",border:"none",borderRadius:10,padding:"8px 14px",
                  cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:12,color:"#DC2626"}}>
                Delete
              </button>
            </div>

            {[
              {l:"Outcome", v:selected.outcome?.toUpperCase(), c:selected.outcome==="profit"?"#16A34A":selected.outcome==="loss"?"#DC2626":"#D97706"},
              {l:"P&L", v:(parseFloat(selected.pnl)>=0?"+":"")+fmt(parseFloat(selected.pnl||0)), c:parseFloat(selected.pnl||0)>=0?"#16A34A":"#DC2626"},
              {l:"Entry", v:selected.entry?"₹"+selected.entry:"—"},
              {l:"Exit",  v:selected.exit?"₹"+selected.exit:"—"},
            ].map(r=>(
              <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${tm.border}`}}>
                <span style={{fontSize:13,color:tm.textSub,fontWeight:700}}>{r.l}</span>
                <span style={{fontSize:13,fontWeight:800,color:r.c||tm.text}}>{r.v}</span>
              </div>
            ))}

            {selected.notes && <div style={{marginTop:16}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>📝 Notes</div>
              <div style={{fontSize:13,color:tm.text,lineHeight:1.7,background:tm.cardAlt,padding:12,borderRadius:12}}>{selected.notes}</div>
            </div>}
            {selected.mistakes && <div style={{marginTop:12}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>⚠️ Mistakes</div>
              <div style={{fontSize:13,color:tm.text,lineHeight:1.7,background:"#FEF2F2",padding:12,borderRadius:12}}>{selected.mistakes}</div>
            </div>}
            {selected.lesson && <div style={{marginTop:12}}>
              <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:6}}>💡 Lesson</div>
              <div style={{fontSize:13,color:tm.text,lineHeight:1.7,background:"#F0FDF4",padding:12,borderRadius:12}}>{selected.lesson}</div>
            </div>}
          </div>
        </div>
      )}
    </div>
  );
}
